"use client";

import React, { useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Breadcrumb } from "@/components/common/Breadcrumb";
import { ChevronLeft, UserCheck } from "lucide-react";
import { StandardFormInput } from "@/components/common/StandardFormInput";
import FormFieldWrapper from "@/components/coupon/formFieldWrapper";
import {
  ButtonPrimary,
  ButtonOutline,
} from "@/components/common/StandardButton";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { UpdateCustomerDto } from "@/types/models/customer";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/store/store";
import {
  fetchCustomerRequest,
  updateCustomerRequest,
  resetFormState,
} from "@/store/slices/customer.slice";

// Form validation schema
const customerSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
});

const EditCustomer: React.FC = () => {
  const { storeType, storeId, customerId } = useParams<{
    storeType: string;
    storeId: string;
    customerId: string;
  }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const {
    currentCustomer: customer,
    customerLoading,
    customerError,
    formState,
  } = useSelector((state: RootState) => state.customers);

  type CustomerFormValues = z.infer<typeof customerSchema>;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CustomerFormValues>({
    resolver: zodResolver(customerSchema),
    mode: "onSubmit",
  });

  // Fetch customer on mount
  useEffect(() => {
    if (customerId) {
      dispatch(fetchCustomerRequest({ customerId }));
    }
  }, [customerId, dispatch]);

  // Populate form when customer data is loaded
  useEffect(() => {
    if (customer) {
      reset({
        first_name: customer.first_name || "",
        last_name: customer.last_name || "",
        email: customer.email || "",
      });
    }
  }, [customer, reset]);

  // Handle form success
  useEffect(() => {
    if (formState.success) {
      toast.success("Customer updated successfully");
      dispatch(resetFormState());
      navigate(`/${storeType}/${storeId}/customers/${customerId}`);
    }
  }, [formState.success, dispatch, navigate, storeType, storeId, customerId]);

  // Handle form error
  useEffect(() => {
    if (formState.error) {
      toast.error(formState.error.message || "Failed to update customer");
    }
  }, [formState.error]);

  const onSubmit: SubmitHandler<UpdateCustomerDto> = (data) => {
    if (!customerId) {
      toast.error("Missing customerId");
      return;
    }
    dispatch(updateCustomerRequest({ customerId, customer: data }));
  };

  const customerName = customer
    ? `${customer.first_name || ""} ${customer.last_name || ""}`.trim() ||
      "Unknown Customer"
    : "Customer";

  const breadcrumbItems = [
    {
      label: "Customers",
      href: `/${storeType}/${storeId}/customers`,
    },
    {
      label: customerName,
      href: `/${storeType}/${storeId}/customers/${customerId}`,
    },
    {
      label: "Edit",
      isActive: true,
    },
  ];

  if (customerLoading) {
    return (
      <div className="p-2 mb-6 relative">
        <div className="mb-4">
          <Breadcrumb items={breadcrumbItems} showBackButton={false} />
        </div>
        <div className="space-y-4 animate-pulse">
          <div className="h-8 bg-muted rounded w-64"></div>
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="h-4 bg-muted rounded w-32"></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="h-10 bg-muted rounded"></div>
                <div className="h-10 bg-muted rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (customerError || !customer) {
    return (
      <div className="p-2 mb-6 relative">
        <div className="mb-4">
          <Breadcrumb items={breadcrumbItems} showBackButton={false} />
        </div>
        <div className="text-center text-destructive">
          {customerError?.message || "Customer not found"}
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 mb-6 relative">
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Breadcrumb */}
        <div className="mb-4">
          <Breadcrumb items={breadcrumbItems} showBackButton={false} />
        </div>

        {/* Top menu */}
        <div className="flex items-center justify-between w-full mb-4">
          <div className="flex items-center justify-between gap-0">
            <Link to={`/${storeType}/${storeId}/customers/${customerId}`}>
              <button
                type="button"
                className="flex items-center px-3 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary/80 rounded-r-none box-border h-10 cursor-pointer"
              >
                <ChevronLeft size={18} className="mr-1" /> Back
              </button>
            </Link>
            <span className="px-3 py-2 bg-card text-foreground rounded-full rounded-l-none border border-border box-border h-10">
              Edit {customerName}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <ButtonOutline
              type="button"
              onClick={() => navigate(-1)}
              disabled={formState.loading}
            >
              Cancel
            </ButtonOutline>
            <ButtonPrimary
              type="submit"
              className="rounded-full"
              disabled={formState.loading}
            >
              <UserCheck size={16} className="mr-1" />
              {formState.loading ? "Updating..." : "Update Customer"}
            </ButtonPrimary>
          </div>
        </div>

        {/* Form Content */}
        <div className="space-y-6">
          {/* Personal Information */}
          <FormFieldWrapper title="Personal Information">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StandardFormInput
                label="First Name"
                placeholder="Enter first name"
                {...register("first_name")}
                error={errors.first_name?.message}
                required
              />
              <StandardFormInput
                label="Last Name"
                placeholder="Enter last name"
                {...register("last_name")}
                error={errors.last_name?.message}
                required
              />
              <StandardFormInput
                label="Email"
                placeholder="Enter email (optional)"
                type="email"
                {...register("email")}
                error={errors.email?.message}
              />
            </div>
            <div className="mt-4 p-3 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong>Note:</strong> Phone number cannot be changed. Contact
                support if you need to update the phone number.
              </p>
              {customer.phone_number && (
                <p className="text-sm text-foreground mt-1">
                  Current phone: {customer.phone_number}
                </p>
              )}
            </div>
          </FormFieldWrapper>
        </div>

        {formState.error && (
          <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-sm text-destructive">
              {formState.error.message}
            </p>
          </div>
        )}
      </form>
    </div>
  );
};

export default EditCustomer;
